import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応（既存remotionコンポジション踏襲）
import { Player, PlayerRef } from '@remotion/player';
import {
  CaptionSegment,
  CaptionStyle,
  UgcAdProject,
  UgcAdScene,
  UgcAdTimeline,
  TelopPosition,
  TelopSize,
} from '../../services/api/ugcAdService';
import { TaskyButton, TaskyTextarea } from '../tasky';
import { cn } from '../../lib/utils';
import {
  UgcAdComposition,
  deriveUgcCompositionScenes,
  deriveUgcCtaCard,
  getUgcAdDimensions,
  getUgcAdDurationInFrames,
  UGC_AD_FPS,
  CAPTION_STYLE_OPTIONS,
  CAPTION_POSITION_OPTIONS,
  CAPTION_SIZE_OPTIONS,
  stabilizeUgcCompositionMedia,
  orderedIncludedScenes,
} from '../../remotion/ugc-ad';
// 映像トラック（シーンの尺・並び）編集の純ロジック。クリップ実尺の上限もここが持つ
import {
  SceneDurations,
  maxSceneSeconds,
  reorder,
  resizeSceneDuration,
  resolveSceneSeconds,
  resolveTotalSeconds,
} from './sceneTimelineEditor';
// テロップ編集の純ロジック（Undo/Redo・分割・クランプ）は song-ad と共通。
// CaptionSegment は両サービスで同一契約のため構造的に互換（変更する場合は両方を追従させること）
import {
  captionHistoryReducer,
  createCaptionAt,
  initCaptionHistory,
  moveCaption,
  resizeCaptionEnd,
  resizeCaptionStart,
  splitCaptionAt,
} from '../song-ad/timelineEditor';
import {
  ArrowLeft,
  Download,
  Loader2,
  Pause,
  Play,
  Plus,
  Redo2,
  Scan,
  Scissors,
  Subtitles,
  Trash2,
  Undo2,
  Video,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

/**
 * 詳細編集（タイムラインエディタ。song-ad EditorStep 踏襲）。
 *
 * timeline.captions（テロップ配列）だけをこの画面で編集する。シーンの並び替え・モーション設定は
 * ④ストーリーボード/⑤モーションで完結しており重複させない（意図的な範囲限定）。
 * song-ad との差分: 楽曲トラックが無い（UGCの音声はクリップ内の発話。トラックとしては編集できない）ため、
 * タイムラインは 映像/テロップ の2トラック構成。
 *
 * Undo/Redo・分割・ドラッグ移動/伸縮のクランプは song-ad/timelineEditor.ts の純関数を共用。
 * 自動保存は2秒debounce。保存中/保存済み/エラーをツールバーに表示する。
 */

interface EditorStepProps {
  project: UgcAdProject;
  scenes: UgcAdScene[];
  onSaveTimeline: (timeline: UgcAdTimeline) => Promise<void>;
  onBackToPreview: () => void;
  onProceedToExport: () => void;
}

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DEBOUNCE_MS = 2000;
/** 再生ヘッド更新の最小間隔（秒）。毎フレームsetStateするとメインスレッドが詰まる（song-ad v8.1） */
const PLAYHEAD_UPDATE_SEC = 0.1;
const TEXT_COMMIT_DEBOUNCE_MS = 600;
const MIN_PX_PER_SEC = 6;
const MAX_PX_PER_SEC = 140;

const CAPTION_BLOCK_CLASS: Record<CaptionStyle, string> = {
  simple: 'bg-indigo-50 border-indigo-300 text-indigo-700',
  pop: 'bg-pink-50 border-pink-300 text-pink-700',
  impact: 'bg-amber-50 border-amber-400 text-amber-800',
  premium: 'bg-slate-100 border-slate-400 text-slate-700',
};

const buildDefaultTimeline = (scenes: UgcAdScene[]): UgcAdTimeline => ({
  captions: [],
  scene_order: [...scenes].sort((a, b) => a.display_order - b.display_order).map((s) => s.id),
  motion: Object.fromEntries(scenes.map((s) => [s.id, { mode: s.motion_mode, prompt: s.motion_prompt }])),
  updated_client_at: '',
});

const formatSec = (sec: number): string => {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const rem = (s % 60).toFixed(1).padStart(4, '0');
  return `${m}:${rem}`;
};

const EditorStep: React.FC<EditorStepProps> = ({ project, scenes, onSaveTimeline, onBackToPreview, onProceedToExport }) => {
  const baseTimeline = useMemo(() => project.timeline ?? buildDefaultTimeline(scenes), [project, scenes]);

  // Undo/Redoはテロップ配列だけを対象にする（シーン順・モーションはこの画面で編集しない）
  const [history, dispatchHistory] = useReducer(captionHistoryReducer, baseTimeline.captions, initCaptionHistory);
  // ドラッグ中/連続入力中の即時プレビュー（履歴には積まない）。nullなら history.present を表示
  const [liveCaptions, setLiveCaptions] = useState<CaptionSegment[] | null>(null);
  const captions = liveCaptions ?? history.present;

  /* ---------- シーン（映像トラック）の尺・並び ---------- */

  // 保存済みの値と、ドラッグ中の即時プレビュー。テロップと同じ二段構え
  const [liveSceneDurations, setLiveSceneDurations] = useState<SceneDurations | null>(null);
  const [liveSceneOrder, setLiveSceneOrder] = useState<string[] | null>(null);
  const sceneDurations = liveSceneDurations ?? baseTimeline.scene_durations;
  const sceneOrder = liveSceneOrder ?? baseTimeline.scene_order;

  const workingTimeline = useMemo(
    () => ({
      ...baseTimeline,
      captions,
      scene_order: sceneOrder,
      scene_durations: sceneDurations,
    }),
    [baseTimeline, captions, sceneOrder, sceneDurations]
  );

  /** 再生順・skip除外済みのシーン列。プレビューと同じ実装を使う */
  const timelineScenes = useMemo(
    () => orderedIncludedScenes(scenes, workingTimeline).map((entry) => entry.scene),
    [scenes, workingTimeline]
  );

  // シーンの尺を手で変えていれば動画全体の長さもそれに追従する（総尺可変）
  const totalDuration = useMemo(
    () => resolveTotalSeconds(timelineScenes, sceneDurations, project.target_seconds),
    [timelineScenes, sceneDurations, project.target_seconds]
  );

  /**
   * CTAカードに尺を貸す前の、各シーンの本来の尺。
   *
   * 映像トラックの表示は CTA 借用後（renderScenes）だが、リサイズの基準にはこちらを使う。
   * 借用後の値で保存すると末尾シーンが掴むたびに縮む（総尺の侵食）。
   */
  const contentSeconds = useMemo(
    () => resolveSceneSeconds(timelineScenes, sceneDurations, project.target_seconds),
    [timelineScenes, sceneDurations, project.target_seconds]
  );

  const [selectedCaptionId, setSelectedCaptionId] = useState<string | null>(null);
  const [pxPerSec, setPxPerSec] = useState(24);
  const [currentSec, setCurrentSec] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const playerRef = useRef<PlayerRef | null>(null);
  // Playerはkey変更で再マウントされるため、コールバックrefでインスタンスの着脱を検知し
  // 購読effectのdepsに含めて追従させる（song-ad v4原因分析④と同じ対策）
  const [playerVersion, setPlayerVersion] = useState(0);
  const attachPlayerRef = useCallback((instance: PlayerRef | null) => {
    playerRef.current = instance;
    setPlayerVersion((v) => v + 1);
  }, []);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDirtyRef = useRef(false);

  const compositionScenes = useMemo(
    () => deriveUgcCompositionScenes(scenes, workingTimeline, totalDuration),
    [scenes, workingTimeline, totalDuration]
  );
  const { scenes: renderScenes, cta } = useMemo(
    () => deriveUgcCtaCard(compositionScenes, project, totalDuration),
    [compositionScenes, project, totalDuration]
  );
  const dimensions = getUgcAdDimensions();
  const durationInFrames = getUgcAdDurationInFrames(totalDuration);

  /**
   * Player の再マウント判定に使うフレーム数。
   *
   * `durationInFrames` はドラッグ中も毎フレーム変わるため、そのまま key にすると
   * Player が作り直されて再生位置が飛ぶ。ドラッグが終わってから追従させる。
   */
  const [committedDurationInFrames, setCommittedDurationInFrames] = useState(durationInFrames);
  const isResizingSceneRef = useRef(false);
  // mouseup のクロージャは張った時点の totalDuration を捕まえてしまうため ref で最新を読む
  const totalDurationRef = useRef(totalDuration);

  useEffect(() => {
    totalDurationRef.current = totalDuration;
    if (isResizingSceneRef.current) return;
    setCommittedDurationInFrames(durationInFrames);
  }, [durationInFrames, totalDuration]);
  const stableMedia = useMemo(
    () => stabilizeUgcCompositionMedia(renderScenes, cta, null),
    [renderScenes, cta]
  );

  /* ---------- Player <-> 再生ヘッド同期 ---------- */

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return undefined;
    const handleFrameUpdate = (e: { detail: { frame: number } }) => {
      const sec = e.detail.frame / UGC_AD_FPS;
      setCurrentSec((prev) => (Math.abs(sec - prev) >= PLAYHEAD_UPDATE_SEC ? sec : prev));
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    player.addEventListener('frameupdate', handleFrameUpdate);
    player.addEventListener('play', handlePlay);
    player.addEventListener('pause', handlePause);
    return () => {
      player.removeEventListener('frameupdate', handleFrameUpdate);
      player.removeEventListener('play', handlePlay);
      player.removeEventListener('pause', handlePause);
    };
  }, [playerVersion]);

  const seekToSec = useCallback((sec: number) => {
    const frame = Math.round(Math.max(0, Math.min(totalDuration, sec)) * UGC_AD_FPS);
    playerRef.current?.seekTo(frame);
    setCurrentSec(frame / UGC_AD_FPS);
  }, [totalDuration]);

  /* ---------- 自動保存（2秒debounce） ---------- */

  /**
   * 未保存の編集を即座に書き込む。
   *
   * debounce の待ち時間中に画面を離れると、cleanup が clearTimeout するだけで保存が
   * 捨てられ、書き出しがサーバー上の古い timeline で走ってしまう。離脱の前に必ず通す。
   */
  const buildTimeline = useCallback(
    (): UgcAdTimeline => ({
      ...baseTimeline,
      captions: history.present,
      scene_order: sceneOrder,
      scene_durations: sceneDurations,
      updated_client_at: new Date().toISOString(),
    }),
    [baseTimeline, history.present, sceneOrder, sceneDurations]
  );

  const flushAutosave = useCallback(async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (!isDirtyRef.current) return;

    setSaveStatus('saving');
    try {
      await onSaveTimeline(buildTimeline());
      isDirtyRef.current = false;
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [buildTimeline, onSaveTimeline]);

  useEffect(() => {
    if (!isDirtyRef.current) return undefined;
    setSaveStatus('pending');
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      setSaveStatus('saving');
      onSaveTimeline(buildTimeline())
        .then(() => setSaveStatus('saved'))
        .catch(() => setSaveStatus('error'));
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // シーンの尺・並びもテロップと同じ自動保存に乗せる（ドラッグ中の連続更新はdebounceが吸収する）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.present, liveSceneDurations, liveSceneOrder]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    if (textCommitTimerRef.current) clearTimeout(textCommitTimerRef.current);
  }, []);

  /* ---------- 履歴コミット/プレビューのヘルパー ---------- */

  const commit = useCallback((next: CaptionSegment[]) => {
    isDirtyRef.current = true;
    dispatchHistory({ type: 'commit', captions: next });
    setLiveCaptions(null);
  }, []);

  const previewLive = useCallback((next: CaptionSegment[]) => {
    setLiveCaptions(next);
  }, []);

  const scheduleTextCommit = useCallback((next: CaptionSegment[]) => {
    previewLive(next);
    if (textCommitTimerRef.current) clearTimeout(textCommitTimerRef.current);
    textCommitTimerRef.current = setTimeout(() => commit(next), TEXT_COMMIT_DEBOUNCE_MS);
  }, [commit, previewLive]);

  const flushTextCommit = useCallback(() => {
    if (textCommitTimerRef.current) {
      clearTimeout(textCommitTimerRef.current);
      textCommitTimerRef.current = null;
      if (liveCaptions) commit(liveCaptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCaptions, commit]);

  const handleUndo = useCallback(() => {
    flushTextCommit();
    isDirtyRef.current = true;
    dispatchHistory({ type: 'undo' });
  }, [flushTextCommit]);

  const handleRedo = useCallback(() => {
    flushTextCommit();
    isDirtyRef.current = true;
    dispatchHistory({ type: 'redo' });
  }, [flushTextCommit]);

  const selectCaption = useCallback((id: string | null) => {
    flushTextCommit();
    setSelectedCaptionId(id);
  }, [flushTextCommit]);

  /* ---------- テロップ操作 ---------- */

  const selected = captions.find((c) => c.id === selectedCaptionId) ?? null;

  const updateSelected = useCallback((patch: Partial<CaptionSegment>) => {
    if (!selected) return;
    const next = captions.map((c) => (c.id === selected.id ? { ...c, ...patch } : c));
    commit(next);
  }, [captions, commit, selected]);

  const handleTextChange = useCallback((text: string) => {
    if (!selected) return;
    const next = captions.map((c) => (c.id === selected.id ? { ...c, text } : c));
    scheduleTextCommit(next);
  }, [captions, scheduleTextCommit, selected]);

  const handleAddCaption = useCallback(() => {
    const created = createCaptionAt(currentSec, totalDuration);
    commit([...captions, created]);
    setSelectedCaptionId(created.id);
  }, [captions, commit, currentSec, totalDuration]);

  const handleDeleteSelected = useCallback(() => {
    if (!selected) return;
    commit(captions.filter((c) => c.id !== selected.id));
    setSelectedCaptionId(null);
  }, [captions, commit, selected]);

  const handleSplitSelected = useCallback(() => {
    if (!selected) return;
    const cursor = textareaRef.current?.selectionStart ?? Math.floor(selected.text.length / 2);
    const result = splitCaptionAt(selected, cursor);
    if (!result) return;
    const [first, second] = result;
    const next = captions.flatMap((c) => (c.id === selected.id ? [first, second] : [c]));
    commit(next);
    setSelectedCaptionId(second.id);
  }, [captions, commit, selected]);

  /* ---------- ドラッグ（移動/端リサイズ） ---------- */

  const startCaptionDrag = useCallback(
    (caption: CaptionSegment, mode: 'move' | 'start' | 'end') => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      selectCaption(caption.id);
      const startX = e.clientX;
      const resultRef = { current: caption };

      const onMove = (ev: MouseEvent) => {
        const deltaSec = (ev.clientX - startX) / pxPerSec;
        let next: CaptionSegment;
        if (mode === 'move') {
          next = moveCaption(caption, deltaSec, totalDuration);
        } else if (mode === 'start') {
          next = resizeCaptionStart(caption, caption.start + deltaSec);
        } else {
          next = resizeCaptionEnd(caption, caption.end + deltaSec, totalDuration);
        }
        resultRef.current = next;
        previewLive(captions.map((c) => (c.id === caption.id ? next : c)));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        commit(captions.map((c) => (c.id === caption.id ? resultRef.current : c)));
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [captions, commit, previewLive, pxPerSec, selectCaption, totalDuration]
  );

  /* ---------- 映像トラック（シーンの尺・並び替え） ---------- */

  /**
   * シーンの右端をドラッグして尺を変える。
   *
   * 生成済みクリップのあるシーンはクリップの実尺が上限（`clampSceneSeconds`）。
   * それ以上伸ばしても合成時に最終フレームの複製で埋まる＝止め絵が続くだけなので、
   * 編集段階で止める。
   */
  const startSceneResize = useCallback(
    (sceneId: string, currentSeconds: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;

      // ドラッグを始めた時点で dirty にする。mouseup で立てても state 変化が無く
      // 自動保存の effect が再実行されないため、尺変更だけ保存されない穴になる
      isDirtyRef.current = true;
      isResizingSceneRef.current = true;

      const onMove = (ev: MouseEvent) => {
        const deltaSec = (ev.clientX - startX) / pxPerSec;
        setLiveSceneDurations(
          resizeSceneDuration(
            timelineScenes,
            sceneDurations,
            project.target_seconds,
            sceneId,
            currentSeconds + deltaSec
          )
        );
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        isResizingSceneRef.current = false;
        // 動かさずにクリックしただけでも保存を確定させる（同じ値でも害はない）
        setLiveSceneDurations((prev) => (prev ? { ...prev } : prev));
        // ドラッグ中は据え置いていた Player の尺をここで追従させる
        setCommittedDurationInFrames(getUgcAdDurationInFrames(totalDurationRef.current));
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [pxPerSec, project.target_seconds, sceneDurations, timelineScenes]
  );

  /** シーンを1つ前／後ろへ動かす（配置の変更） */
  const moveSceneBy = useCallback(
    (sceneId: string, offset: number) => {
      const ids = timelineScenes.map((s) => s.id);
      const from = ids.indexOf(sceneId);
      const to = from + offset;

      if (from < 0 || to < 0 || to >= ids.length) return;

      const reordered = reorder(ids, from, to);

      // skip したシーンは timelineScenes に含まれないので、元の並びの末尾に残して落とさない
      const rest = sceneOrder.filter((id) => !reordered.includes(id));

      setLiveSceneOrder([...reordered, ...rest]);
      isDirtyRef.current = true;
    },
    [sceneOrder, timelineScenes]
  );

  /* ---------- ルーラーシーク ---------- */

  const handleRulerMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const seek = (clientX: number) => seekToSec((clientX - rect.left) / pxPerSec);
    seek(e.clientX);
    const onMove = (ev: MouseEvent) => seek(ev.clientX);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pxPerSec, seekToSec]);

  const timelineWidth = Math.max(1, totalDuration) * pxPerSec;
  const tickCount = Math.floor(totalDuration) + 1;

  const saveStatusLabel: Record<SaveStatus, string> = {
    idle: '',
    pending: '変更あり',
    saving: '保存中...',
    saved: '保存済み',
    error: '保存に失敗しました',
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 260px)', minHeight: 520 }}>
      {/* ツールバー */}
      <div className="flex items-center gap-1 px-2 py-2 border-b border-warm-border bg-white shrink-0 flex-wrap">
        <button
          onClick={() => { void flushAutosave().then(onBackToPreview); }}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 rounded-lg px-2 py-1.5 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          プレビュー
        </button>
        <div className="w-px h-5 bg-warm-border mx-1" />
        <button
          onClick={handleUndo}
          disabled={history.past.length === 0}
          title="元に戻す"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          onClick={handleRedo}
          disabled={history.future.length === 0}
          title="やり直す"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
        >
          <Redo2 className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-warm-border mx-1" />
        <button
          onClick={() => setPxPerSec((v) => Math.max(MIN_PX_PER_SEC, v / 1.5))}
          title="ズームアウト"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <button
          onClick={() => setPxPerSec((v) => Math.min(MAX_PX_PER_SEC, v * 1.5))}
          title="ズームイン"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            const width = timelineScrollRef.current?.clientWidth ?? 800;
            setPxPerSec(Math.max(MIN_PX_PER_SEC, Math.min(MAX_PX_PER_SEC, Math.floor((width - 20) / Math.max(1, totalDuration)))));
          }}
          title="全体表示"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <Scan className="w-4 h-4" />
        </button>

        <div className="flex-1" />

        {saveStatus !== 'idle' && (
          <span
            className={`text-xs flex items-center gap-1 mr-2 ${
              saveStatus === 'error' ? 'text-destructive' : saveStatus === 'saved' ? 'text-green-600' : 'text-gray-400'
            }`}
          >
            {saveStatus === 'saving' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saveStatusLabel[saveStatus]}
          </span>
        )}

        <TaskyButton size="sm" onClick={() => { void flushAutosave().then(onProceedToExport); }}>
          <Download className="w-3.5 h-3.5" />
          書き出す
        </TaskyButton>
      </div>

      {/* メイン3ペイン */}
      <div className="flex-1 flex min-h-0">
        {/* 左：テロップ一覧 */}
        <div className="w-56 border-r border-warm-border flex flex-col shrink-0 bg-white">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-warm-border flex items-center justify-between shrink-0">
            <span className="flex items-center gap-1">
              <Subtitles className="w-3.5 h-3.5" />
              テロップ（{captions.length}）
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {[...captions].sort((a, b) => a.start - b.start).map((c) => (
              <button
                key={c.id}
                data-testid={`caption-list-item-${c.id}`}
                onClick={() => selectCaption(c.id)}
                className={`w-full text-left p-2 rounded-lg mb-1 border ${
                  selectedCaptionId === c.id ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-gray-50'
                }`}
              >
                <p className="text-xs text-gray-700 truncate">{c.text}</p>
                <p className="text-[10px] text-gray-400 tabular-nums">
                  {formatSec(c.start)} – {formatSec(c.end)}
                </p>
              </button>
            ))}
            {captions.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">テロップがありません</p>}
          </div>
          <div className="p-2 border-t border-warm-border shrink-0">
            <TaskyButton variant="outline" size="sm" onClick={handleAddCaption} className="w-full">
              <Plus className="w-3.5 h-3.5" />
              テロップを追加
            </TaskyButton>
          </div>
        </div>

        {/* 中央：ミニプレビュー（height基準でaspect-ratioから幅を算出。song-ad v7の罠対策と同じ） */}
        <div className="flex-1 min-w-0 bg-warm-surface flex items-center justify-center p-3">
          <div
            className="relative bg-black rounded-xl overflow-hidden shadow"
            style={{
              aspectRatio: `${dimensions.width} / ${dimensions.height}`,
              height: '100%',
              maxHeight: '100%',
              width: 'auto',
              maxWidth: '100%',
            }}
          >
            <Player
              ref={attachPlayerRef}
              // ドラッグ中は key を据え置く。尺に追従して毎フレーム key を変えると
              // Player が破棄・再生成され、再生位置のリセットとメディア再読込が起きる
              key={`${committedDurationInFrames}`}
              component={UgcAdComposition}
              inputProps={{
                scenes: stableMedia.scenes,
                captions,
                bgmUrl: stableMedia.bgmUrl,
                cta: stableMedia.cta,
              }}
              durationInFrames={durationInFrames}
              fps={UGC_AD_FPS}
              compositionWidth={dimensions.width}
              compositionHeight={dimensions.height}
              style={{ width: '100%', height: '100%' }}
              acceptableTimeShiftInSeconds={1.5}
              moveToBeginningWhenEnded={false}
            />
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 py-2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
              <button
                onClick={() => (isPlaying ? playerRef.current?.pause() : playerRef.current?.play())}
                className="w-8 h-8 rounded-full bg-white/90 text-dark flex items-center justify-center hover:bg-white transition-colors pointer-events-auto"
              >
                {isPlaying ? <Pause className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current" />}
              </button>
              <span
                className="text-[11px] text-white tabular-nums"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,.7)' }}
              >
                {formatSec(currentSec)} / {formatSec(totalDuration)}
              </span>
            </div>
          </div>
        </div>

        {/* 右：インスペクタ */}
        <div className="w-72 border-l border-warm-border overflow-y-auto shrink-0 bg-white p-4">
          {!selected && (
            <p className="text-xs text-gray-400 text-center mt-8 leading-relaxed">
              タイムラインや左のリストから
              <br />
              テロップを選択すると
              <br />
              ここで編集できます
            </p>
          )}
          {selected && (
            <div>
              <p className="text-sm font-semibold text-dark mb-3">テロップ</p>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">文言（改行できます）</label>
              <TaskyTextarea
                ref={textareaRef}
                value={selected.text}
                onChange={(e) => handleTextChange(e.target.value)}
                onBlur={flushTextCommit}
                rows={3}
                className="mb-3 text-sm"
              />
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">表示開始（秒）</label>
                  <input
                    type="number"
                    step={0.1}
                    min={0}
                    max={totalDuration}
                    value={selected.start}
                    onChange={(e) => updateSelected(resizeCaptionStart(selected, Number(e.target.value)))}
                    className="bg-warm-surface rounded-lg border-none p-2 text-sm w-full"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">表示終了（秒）</label>
                  <input
                    type="number"
                    step={0.1}
                    min={0}
                    max={totalDuration}
                    value={selected.end}
                    onChange={(e) => updateSelected(resizeCaptionEnd(selected, Number(e.target.value), totalDuration))}
                    className="bg-warm-surface rounded-lg border-none p-2 text-sm w-full"
                  />
                </div>
              </div>

              <label className="block text-[11px] font-medium text-gray-500 mb-1">表示位置</label>
              <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border mb-3 text-xs">
                {CAPTION_POSITION_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => updateSelected({ position: opt.key as TelopPosition })}
                    className={`flex-1 py-1.5 transition-colors ${
                      selected.position === opt.key ? 'bg-primary text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <label className="block text-[11px] font-medium text-gray-500 mb-1">サイズ</label>
              <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border mb-3 text-xs">
                {CAPTION_SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => updateSelected({ size: opt.key as TelopSize })}
                    className={`flex-1 py-1.5 transition-colors ${
                      selected.size === opt.key ? 'bg-primary text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <label className="block text-[11px] font-medium text-gray-500 mb-1.5">スタイル</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {CAPTION_STYLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => updateSelected({ style: opt.key as CaptionStyle })}
                    className={`rounded-lg p-2 flex flex-col items-center gap-1 border transition-colors ${
                      selected.style === opt.key ? 'border-primary bg-primary/5' : 'border-warm-border hover:border-gray-300'
                    }`}
                  >
                    <span className={`w-full h-8 rounded flex items-center justify-center text-xs ${CAPTION_BLOCK_CLASS[opt.key as CaptionStyle]}`}>
                      Aa あ
                    </span>
                    <span className="text-[10px] text-gray-500">{opt.label}</span>
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSplitSelected}
                  className="flex-1 flex items-center justify-center gap-1 text-xs text-gray-600 bg-warm-surface hover:bg-gray-100 rounded-lg py-2 transition-colors"
                  title="文言のカーソル位置で分割"
                >
                  <Scissors className="w-3.5 h-3.5" />
                  ここで分割
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center justify-center gap-1 text-xs text-destructive bg-red-50 hover:bg-red-100 rounded-lg px-3 py-2 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
                分割位置は文言内のカーソル位置。表示時間は文字数で自動配分されます。
              </p>
            </div>
          )}
        </div>
      </div>

      {/* タイムライン（映像/テロップの2トラック。UGCに楽曲トラックは無い） */}
      <div className="border-t border-warm-border shrink-0 bg-white flex" style={{ height: 140 }}>
        <div className="w-14 shrink-0 border-r border-warm-border text-[10px] text-gray-400 flex flex-col">
          <div style={{ height: 24 }} />
          <div
            className="flex items-center px-2"
            style={{ height: 44 }}
            title="右端をドラッグで長さ変更、カード上の ◀▶ で並び替え。動画クリップは生成された長さまで伸ばせます"
          >
            <Video className="w-3 h-3 mr-1" />
            映像
          </div>
          <div className="flex items-center px-2" style={{ height: 36 }}>
            <Subtitles className="w-3 h-3 mr-1" />
            テロップ
          </div>
        </div>

        <div ref={timelineScrollRef} className="flex-1 overflow-x-auto overflow-y-hidden relative">
          <div className="relative" style={{ width: timelineWidth + 40 }}>
            {/* ルーラー */}
            <div
              onMouseDown={handleRulerMouseDown}
              className="relative cursor-pointer border-b border-warm-border"
              style={{ height: 24 }}
            >
              {Array.from({ length: tickCount }, (_, s) => (
                <React.Fragment key={s}>
                  <span
                    className={`absolute bottom-0 border-l ${s % 5 === 0 ? 'border-gray-400 h-3' : 'border-gray-200 h-1.5'}`}
                    style={{ left: s * pxPerSec }}
                  />
                  {s % 5 === 0 && (
                    <span className="absolute top-0.5 text-[9px] text-gray-400 tabular-nums" style={{ left: s * pxPerSec + 3 }}>
                      {s}s
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* 映像トラック（右端ドラッグで尺、◀▶で並び替え）。
                renderScenes はCTAカード分だけ末尾を縮めた枠。書き出しと一致させる */}
            <div className="relative border-b border-warm-border/60" style={{ height: 44 }}>
              {renderScenes.map((scene, i) => {
                const source = timelineScenes.find((s) => s.id === scene.id);
                const seconds = scene.endSec - scene.startSec;
                // 表示幅は CTA 借用後（renderScenes）だが、**リサイズの基準は借用前の尺**にする。
                // 借用後の値を基準にすると、末尾シーンを掴むたびに CTA 分（2.5秒）だけ
                // 短い値が保存され、総尺が削られていく
                const baseSeconds = contentSeconds[i] ?? seconds;
                const max = source ? maxSceneSeconds(source) : Infinity;
                const atClipLimit = Number.isFinite(max) && seconds >= max - 0.05;
                const width = Math.max(16, seconds * pxPerSec - 2);

                return (
                  <div
                    key={scene.id}
                    className="absolute top-1 bottom-1 rounded-md overflow-hidden border border-warm-border bg-gray-100 group"
                    style={{
                      left: scene.startSec * pxPerSec,
                      width,
                      backgroundImage: scene.imageUrl ? `url(${scene.imageUrl})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                    title={
                      atClipLimit
                        ? `シーン ${i + 1}（${seconds.toFixed(1)}秒・クリップの長さいっぱい）`
                        : `シーン ${i + 1}（${seconds.toFixed(1)}秒）`
                    }
                  >
                    <button
                      type="button"
                      onClick={() => seekToSec(scene.startSec + 0.05)}
                      className="absolute inset-0 w-full h-full cursor-pointer"
                      aria-label={`シーン ${i + 1} へ移動`}
                    />

                    <span
                      className="absolute top-0.5 left-1 text-[9px] font-bold text-white pointer-events-none"
                      style={{ textShadow: '0 1px 3px rgba(0,0,0,.6)' }}
                    >
                      {i + 1}
                      {scene.motionMode === 'clip' ? ' ▶' : ''}
                    </span>

                    {/* 並び替え（配置）。細い枠でも押せるよう常時表示せずホバーで出す */}
                    {width >= 46 && (
                      <div className="absolute bottom-0.5 left-1 hidden group-hover:flex gap-0.5">
                        <button
                          type="button"
                          disabled={i === 0}
                          onClick={() => moveSceneBy(scene.id, -1)}
                          className="w-4 h-4 rounded bg-black/55 text-white text-[9px] leading-none disabled:opacity-30"
                          aria-label={`シーン ${i + 1} を前へ`}
                        >
                          ◀
                        </button>
                        <button
                          type="button"
                          disabled={i === renderScenes.length - 1}
                          onClick={() => moveSceneBy(scene.id, 1)}
                          className="w-4 h-4 rounded bg-black/55 text-white text-[9px] leading-none disabled:opacity-30"
                          aria-label={`シーン ${i + 1} を後ろへ`}
                        >
                          ▶
                        </button>
                      </div>
                    )}

                    {/* 右端リサイズハンドル。クリップ実尺いっぱいのときは色を変えて理由を示す */}
                    <div
                      role="separator"
                      aria-label={`シーン ${i + 1} の長さを変更`}
                      onMouseDown={startSceneResize(scene.id, baseSeconds)}
                      className={cn(
                        'absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize',
                        atClipLimit ? 'bg-amber-400/80' : 'bg-white/70 hover:bg-primary'
                      )}
                    />
                  </div>
                );
              })}
              {cta && (
                <button
                  onClick={() => seekToSec(cta.start + 0.05)}
                  className="absolute top-1 bottom-1 rounded-md overflow-hidden border border-primary bg-primary/10 flex items-center justify-center"
                  style={{ left: cta.start * pxPerSec, width: Math.max(16, (cta.end - cta.start) * pxPerSec - 2) }}
                  title={`CTA: ${cta.text}`}
                >
                  <span className="text-[9px] font-bold text-primary truncate px-1">CTA</span>
                </button>
              )}
            </div>

            {/* テロップトラック */}
            <div className="relative" style={{ height: 36 }}>
              {captions.map((c) => (
                <div
                  key={c.id}
                  onMouseDown={startCaptionDrag(c, 'move')}
                  className={`absolute top-1 bottom-1 rounded-lg overflow-hidden cursor-pointer select-none border flex items-center px-1.5 ${
                    CAPTION_BLOCK_CLASS[c.style]
                  } ${selectedCaptionId === c.id ? 'ring-2 ring-primary' : ''}`}
                  style={{ left: c.start * pxPerSec, width: Math.max(14, (c.end - c.start) * pxPerSec - 2) }}
                >
                  <span className="text-[9px] truncate pointer-events-none">{c.text}</span>
                  <span
                    onMouseDown={startCaptionDrag(c, 'start')}
                    className="absolute top-0 bottom-0 w-2 cursor-ew-resize"
                    style={{ left: -2 }}
                  />
                  <span
                    onMouseDown={startCaptionDrag(c, 'end')}
                    className="absolute top-0 bottom-0 w-2 cursor-ew-resize"
                    style={{ right: -2 }}
                  />
                </div>
              ))}
            </div>

            {/* 再生ヘッド */}
            <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: currentSec * pxPerSec }}>
              <div className="w-[2px] h-full bg-primary mx-auto" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorStep;
